import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { setToogleHeader } from '../../core/redux/action';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import dayjs from 'dayjs';
import Select from 'react-select';
import { DatePicker } from 'antd';
import {
  ChevronUp,
  RotateCcw,
  Search,
  Download,
  Printer,
  Eye,
  Edit,
  AlertTriangle,
  CheckCircle,
  XCircle,
  TrendingUp,
  TrendingDown,
  Package,
  RefreshCw
} from 'feather-icons-react/build/IconComponents';
import { stockActions } from '../../core/action/stockaction';
import { productActions } from '../../core/action/productActions-bkf-1';

const StockManagement = () => {
  const dispatch = useDispatch();
  const MySwal = withReactContent(Swal);
  const data = useSelector((state) => state.toggle_header);

  // ─── State ───────────────────────────────────────────────────────────────────
  const [products, setProducts] = useState([]);
  const [stockMovements, setStockMovements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [viewMode, setViewMode] = useState('summary'); // 'summary' | 'movements'

  const [filters, setFilters] = useState({
    search: '',
    category: '',
    brand: '',
    stockStatus: 'all',       // client-side only — never sent to backend
    dateFrom: null,
    dateTo: null,
    movementType: 'all'
  });

  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [sortOption, setSortOption] = useState('name_asc');

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1
  });

  // ─── Refs — always hold the latest filter/sort values ────────────────────────
  // FIX: eliminates stale-closure bugs where dropdowns & search used
  //      outdated state captured at mount time.
  const filtersRef = useRef(filters);
  const sortRef    = useRef(sortOption);

  useEffect(() => { filtersRef.current = filters; },    [filters]);
  useEffect(() => { sortRef.current    = sortOption; }, [sortOption]);

  // ─── Dropdown option lists ────────────────────────────────────────────────────
  const stockStatusOptions = [
    { value: 'all',          label: 'All Stock'      },
    { value: 'in_stock',     label: 'In Stock'       },
    { value: 'low_stock',    label: 'Low Stock'      },
    { value: 'out_of_stock', label: 'Out of Stock'   }
  ];

  const movementTypeOptions = [
    { value: 'all',             label: 'All Movements'    },
    { value: 'purchase_in',     label: 'Purchase In'      },
    { value: 'sale_out',        label: 'Sale Out'         },
    { value: 'purchase_return', label: 'Purchase Return'  },
    { value: 'adjustment',      label: 'Stock Adjustment' },
    { value: 'opening_stock',   label: 'Opening Stock'    }
  ];

  const sortOptions = [
    { value: 'name_asc',   label: 'Product Name (A-Z)'    },
    { value: 'name_desc',  label: 'Product Name (Z-A)'    },
    { value: 'stock_asc',  label: 'Stock (Low to High)'   },
    { value: 'stock_desc', label: 'Stock (High to Low)'   },
    { value: 'price_asc',  label: 'Price (Low to High)'   },
    { value: 'price_desc', label: 'Price (High to Low)'   },
    { value: 'newest',     label: 'Newest First'          },
    { value: 'oldest',     label: 'Oldest First'          }
  ];

  // ─── Init ─────────────────────────────────────────────────────────────────────
  useEffect(() => {
    loadInitialData();
    loadStockSummary(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadInitialData = async () => {
    try {
      const [categoriesRes, brandsRes] = await Promise.all([
        productActions.getCategories(),
        productActions.getBrands()
      ]);

      if (categoriesRes.success) {
        setCategories([
          { value: '', label: 'All Categories' },
          ...categoriesRes.data.map(cat => ({
            value: cat.id,
            label: cat.category_name || cat.name
          }))
        ]);
      }

      if (brandsRes.success) {
        setBrands([
          { value: '', label: 'All Brands' },
          ...brandsRes.data.map(brand => ({
            value: brand.id,
            label: brand.brand_name || brand.name
          }))
        ]);
      }
    } catch (error) {
      console.error('Error loading initial data:', error);
    }
  };

  // ─── Core data loader ─────────────────────────────────────────────────────────
  // FIX: reads from refs so it always uses the CURRENT filter/sort values,
  //      regardless of when (or how) it was called.
  // NOTE: stock_status is intentionally NOT sent to the backend — the PHP
  //       stock_status filter has a bug where it double-applies offset/pagination
  //       on an already-paginated result set, producing wrong totals.
  //       We filter client-side via `filteredProducts` instead.
  const loadStockSummary = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      const currentFilters = filtersRef.current;
      const currentSort    = sortRef.current;

      const params = {
        page,
        limit: pagination.limit,
        ...(currentFilters.search    && { search:      currentFilters.search    }),
        ...(currentFilters.category  && { category_id: currentFilters.category  }),
        ...(currentFilters.brand     && { brand_id:    currentFilters.brand     }),
        sort: currentSort
        // stock_status intentionally omitted — filtered client-side
      };

      const result = await stockActions.getStockSummary(params);

      if (result.success) {
        setProducts(result.data || []);
        setPagination(prev => ({
          ...prev,
          page:       result.page       || page,
          total:      result.total      || 0,
          totalPages: result.totalPages || 1
        }));
      } else {
        MySwal.fire({ icon: 'error', title: 'Error', text: result.message || 'Failed to load stock data', timer: 2000 });
      }
    } catch (error) {
      console.error('Error loading stock summary:', error);
      MySwal.fire({ icon: 'error', title: 'Error', text: 'Failed to load stock data', timer: 2000 });
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.limit]);

  // ─── Stock movements loader ───────────────────────────────────────────────────
  const loadStockMovements = async (productId, page = 1) => {
    try {
      setLoading(true);
      const currentFilters = filtersRef.current;

      const params = {
        product_id: productId,
        page,
        limit: 20,
        ...(currentFilters.dateFrom && { from_date: dayjs(currentFilters.dateFrom).format('YYYY-MM-DD') }),
        ...(currentFilters.dateTo   && { to_date:   dayjs(currentFilters.dateTo).format('YYYY-MM-DD')   }),
        ...(currentFilters.movementType !== 'all' && { type: currentFilters.movementType })
      };

      const result = await stockActions.getStockMovements(params);

      if (result.success) {
        setStockMovements(result.data || []);
        // FIX: use functional updater so we always get the freshest products list
        setSelectedProduct(prev =>
          products.find(p => p.id === productId) || prev
        );
        setViewMode('movements');
      } else {
        MySwal.fire({ icon: 'error', title: 'Error', text: result.message || 'Failed to load movements', timer: 2000 });
      }
    } catch (error) {
      console.error('Error loading stock movements:', error);
      MySwal.fire({ icon: 'error', title: 'Error', text: 'Failed to load stock movements', timer: 2000 });
    } finally {
      setLoading(false);
    }
  };

  // ─── Filter handlers — each syncs the ref immediately before calling load ─────

  // FIX: onChange only updates state; ref sync happens here so the next
  //      loadStockSummary call (triggered by Enter / button) sees the new value.
  const handleSearchChange = (e) => {
    const newFilters = { ...filtersRef.current, search: e.target.value };
    setFilters(newFilters);
    filtersRef.current = newFilters;
  };

  const handleSearchSubmit = () => loadStockSummary(1);

  // FIX: Category — sync ref then load
  const handleCategoryChange = (opt) => {
    const newFilters = { ...filtersRef.current, category: opt?.value || '' };
    setFilters(newFilters);
    filtersRef.current = newFilters;
    loadStockSummary(1);
  };

  // FIX: Brand — sync ref then load
  const handleBrandChange = (opt) => {
    const newFilters = { ...filtersRef.current, brand: opt?.value || '' };
    setFilters(newFilters);
    filtersRef.current = newFilters;
    loadStockSummary(1);
  };

  // FIX: Stock Status is purely client-side — only update state, NO API call.
  //      This avoids the broken server-side stock_status filtering in stock_summary.php
  //      (which re-applies offset/limit on an already-paginated result).
  const handleStockStatusChange = (opt) => {
    const newFilters = { ...filtersRef.current, stockStatus: opt?.value || 'all' };
    setFilters(newFilters);
    filtersRef.current = newFilters;
    // No loadStockSummary — filteredProducts handles this instantly
  };

  // FIX: Sort — sync ref then load
  const handleSortChange = (opt) => {
    const newSort = opt?.value || 'name_asc';
    setSortOption(newSort);
    sortRef.current = newSort;
    loadStockSummary(1);
  };

  // FIX: Reset — sync BOTH refs then load
  const handleReset = () => {
    const resetFilters = {
      search: '',
      category: '',
      brand: '',
      stockStatus: 'all',
      dateFrom: null,
      dateTo: null,
      movementType: 'all'
    };
    setFilters(resetFilters);
    setSortOption('name_asc');
    filtersRef.current = resetFilters;
    sortRef.current    = 'name_asc';
    loadStockSummary(1);
  };

  // ─── Stock adjustment ─────────────────────────────────────────────────────────
  const handleAdjustStock = (product) => {
    MySwal.fire({
      title: 'Adjust Stock',
      html: `
        <div class="text-start">
          <p><strong>Product:</strong> ${product.product_name}</p>
          <p><strong>Current Stock:</strong> ${product.current_stock} ${product.unit}</p>
          <hr>
          <div class="mb-3">
            <label class="form-label">Adjustment Type</label>
            <select id="adjustment-type" class="form-select">
              <option value="add">Add Stock (+)</option>
              <option value="remove">Remove Stock (-)</option>
              <option value="set">Set to Exact Quantity</option>
            </select>
          </div>
          <div class="mb-3">
            <label class="form-label">Quantity</label>
            <input type="number" id="adjustment-qty" class="form-control" min="0" step="0.01" value="0">
          </div>
          <div class="mb-3">
            <label class="form-label">Reason</label>
            <input type="text" id="adjustment-reason" class="form-control" placeholder="e.g., Physical count, Damaged, etc.">
          </div>
          <div class="mb-3">
            <label class="form-label">Reference No.</label>
            <input type="text" id="adjustment-ref" class="form-control" placeholder="Optional">
          </div>
        </div>
      `,
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Update Stock',
      cancelButtonText: 'Cancel',
      preConfirm: () => {
        const type      = document.getElementById('adjustment-type').value;
        const qty       = parseFloat(document.getElementById('adjustment-qty').value) || 0;
        const reason    = document.getElementById('adjustment-reason').value;
        const reference = document.getElementById('adjustment-ref').value;

        // FIX: allow qty === 0 only for 'set' type; require > 0 otherwise
        if (qty < 0) {
          Swal.showValidationMessage('Quantity cannot be negative');
          return false;
        }
        if (qty === 0 && type !== 'set') {
          Swal.showValidationMessage('Quantity must be greater than 0');
          return false;
        }

        return { type, qty, reason, reference };
      }
    }).then(async (swalResult) => {
      if (!swalResult.isConfirmed) return;

      try {
        setLoading(true);
        const { type, qty, reason, reference } = swalResult.value;

        let adjustmentQty = qty;
        if (type === 'remove') {
          adjustmentQty = -qty;
        } else if (type === 'set') {
          adjustmentQty = qty - product.current_stock;
        }

        const newStock = product.current_stock + adjustmentQty;

        const adjustmentData = {
          product_id:   product.id,
          quantity:     adjustmentQty,
          type:         'adjustment',
          reason:       reason    || 'Manual stock adjustment',
          reference_no: reference || `ADJ-${Date.now()}`,
          notes:        `Adjusted from ${product.current_stock} to ${newStock}`,
          created_by:   'Admin'
        };

        const adjustResult = await stockActions.adjustStock(adjustmentData);

        if (adjustResult.success) {
          MySwal.fire({ icon: 'success', title: 'Stock Adjusted!', text: 'Stock updated successfully', timer: 2000 });
          loadStockSummary(pagination.page);
        } else {
          MySwal.fire({ icon: 'error', title: 'Error', text: adjustResult.message || 'Failed to adjust stock', timer: 2000 });
        }
      } catch (error) {
        console.error('Error adjusting stock:', error);
        MySwal.fire({ icon: 'error', title: 'Error', text: 'Failed to adjust stock: ' + error.message, timer: 2000 });
      } finally {
        setLoading(false);
      }
    });
  };

  // ─── View movements ───────────────────────────────────────────────────────────
  const handleViewMovements = (product) => {
    setSelectedProduct(product);
    loadStockMovements(product.id);
  };

  const handleBackToSummary = () => {
    setViewMode('summary');
    setSelectedProduct(null);
    setStockMovements([]);
  };

  // ─── Client-side stock-status filter ─────────────────────────────────────────
  // Applied AFTER the API returns data; avoids the broken backend filter.
  const filteredProducts = products.filter(p => {
    const stock    = p.current_stock  || 0;
    const alertQty = p.alert_quantity || 10;
    switch (filters.stockStatus) {
      case 'in_stock':     return stock > alertQty;
      case 'low_stock':    return stock > 0 && stock <= alertQty;
      case 'out_of_stock': return stock <= 0;
      default:             return true;
    }
  });

  // ─── Summary stats (always computed from full `products` list) ────────────────
  const getSummaryStats = () => {
    return products.reduce((acc, p) => {
      const stock    = p.current_stock  || 0;
      const alertQty = p.alert_quantity || 10;
      acc.totalProducts++;
      acc.totalItems += stock;
      acc.totalValue += stock * (p.cost_price || 0);
      if (stock <= 0)             acc.outOfStockCount++;
      else if (stock <= alertQty) acc.lowStockCount++;
      return acc;
    }, { totalProducts: 0, totalItems: 0, totalValue: 0, lowStockCount: 0, outOfStockCount: 0 });
  };

  const stats = getSummaryStats();

  // ─── Helpers ──────────────────────────────────────────────────────────────────
  const getStockStatusBadge = (product) => {
    const stock    = product.current_stock  || 0;
    const alertQty = product.alert_quantity || 10;
    if (stock <= 0)             return { class: 'bg-danger',              icon: <XCircle       size={14} className="me-1" />, text: 'Out of Stock'       };
    if (stock <= alertQty)      return { class: 'bg-warning text-dark',   icon: <AlertTriangle size={14} className="me-1" />, text: `Low Stock (${stock})` };
    return                             { class: 'bg-success',             icon: <CheckCircle   size={14} className="me-1" />, text: `In Stock (${stock})`  };
  };

  const getMovementTypeBadge = (type) => {
    const map = {
      purchase_in:     { class: 'bg-success',   icon: <TrendingUp  size={14} />, text: 'Purchase In' },
      sale_out:        { class: 'bg-danger',    icon: <TrendingDown size={14} />, text: 'Sale Out'    },
      purchase_return: { class: 'bg-warning',   icon: <RefreshCw   size={14} />, text: 'Return'      },
      adjustment:      { class: 'bg-info',      icon: <Edit        size={14} />, text: 'Adjustment'  },
      opening_stock:   { class: 'bg-secondary', icon: <Package     size={14} />, text: 'Opening'     }
    };
    return map[type] || { class: 'bg-secondary', icon: <Package size={14} />, text: type };
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2 }).format(amount || 0);

  const formatDate = (dateString) =>
    dateString ? dayjs(dateString).format('DD/MM/YYYY HH:mm') : 'N/A';

  const renderTooltip = (props, text) => (
    <Tooltip id={`tooltip-${text}`} {...props}>{text}</Tooltip>
  );

  // ─── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="page-wrapper">
      <div className="content">

        {/* ── Header ─────────────────────────────────────────────────────────── */}
        <div className="page-header">
          <div className="page-title">
            <h4>Stock Management</h4>
            <h6>
              {viewMode === 'summary'
                ? 'Monitor and manage inventory levels'
                : `Stock Movements — ${selectedProduct?.product_name}`}
            </h6>
          </div>

          <ul className="table-top-head">
            {viewMode === 'movements' && (
              <li>
                <button className="btn btn-outline-secondary" onClick={handleBackToSummary}>
                  <Package size={16} className="me-1" /> Back to Summary
                </button>
              </li>
            )}
            <li>
              <OverlayTrigger placement="top" overlay={(p) => renderTooltip(p, 'Export')}>
                <Link onClick={() => window.print()}><Download /></Link>
              </OverlayTrigger>
            </li>
            <li>
              <OverlayTrigger placement="top" overlay={(p) => renderTooltip(p, 'Print')}>
                <Link onClick={() => window.print()}><Printer /></Link>
              </OverlayTrigger>
            </li>
            <li>
              <OverlayTrigger placement="top" overlay={(p) => renderTooltip(p, 'Refresh')}>
                <Link onClick={() =>
                  viewMode === 'summary'
                    ? loadStockSummary(pagination.page)
                    : loadStockMovements(selectedProduct?.id)
                }>
                  <RotateCcw />
                </Link>
              </OverlayTrigger>
            </li>
            <li>
              <OverlayTrigger placement="top" overlay={(p) => renderTooltip(p, 'Collapse')}>
                <Link
                  className={data ? 'active' : ''}
                  onClick={() => dispatch(setToogleHeader(!data))}
                >
                  <ChevronUp />
                </Link>
              </OverlayTrigger>
            </li>
          </ul>
        </div>

        {/* ══════════════════════════════════════════════════════════════════════ */}
        {viewMode === 'summary' ? (
          <>
            {/* ── Summary Cards ──────────────────────────────────────────────── */}
            <div className="row mb-4">
              <div className="col-xl-3 col-sm-6">
                <div className="card">
                  <div className="card-body">
                    <h6 className="card-title text-muted">Total Products</h6>
                    <h3 className="mb-0">{stats.totalProducts}</h3>
                    <small className="text-muted">Active products</small>
                  </div>
                </div>
              </div>
              <div className="col-xl-3 col-sm-6">
                <div className="card">
                  <div className="card-body">
                    <h6 className="card-title text-muted">Total Items</h6>
                    <h3 className="mb-0">{stats.totalItems}</h3>
                    <small className="text-muted">Units in stock</small>
                  </div>
                </div>
              </div>
              <div className="col-xl-3 col-sm-6">
                <div className="card">
                  <div className="card-body">
                    <h6 className="card-title text-muted">Stock Value</h6>
                    <h3 className="mb-0 text-primary">{formatCurrency(stats.totalValue)}</h3>
                    <small className="text-muted">At cost price</small>
                  </div>
                </div>
              </div>
              <div className="col-xl-3 col-sm-6">
                <div className="card">
                  <div className="card-body">
                    <h6 className="card-title text-muted">Low / Out of Stock</h6>
                    <h3 className="mb-0">
                      <span className="text-warning me-2">{stats.lowStockCount} Low</span>
                      <span className="text-danger">{stats.outOfStockCount} Out</span>
                    </h3>
                  </div>
                </div>
              </div>
            </div>

            {/* ── Filters ────────────────────────────────────────────────────── */}
            <div className="card mb-4">
              <div className="card-body">
                <div className="row">

                  {/* Search */}
                  <div className="col-lg-3">
                    <div className="mb-3">
                      <label className="form-label">Search</label>
                      <div className="input-group">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Product name, SKU…"
                          value={filters.search}
                          onChange={handleSearchChange}
                          onKeyPress={(e) => e.key === 'Enter' && handleSearchSubmit()}
                        />
                        <button className="btn btn-primary" onClick={handleSearchSubmit}>
                          <Search size={16} />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Category */}
                  <div className="col-lg-2">
                    <div className="mb-3">
                      <label className="form-label">Category</label>
                      <Select
                        options={categories}
                        value={categories.find(c => c.value === filters.category) || null}
                        onChange={handleCategoryChange}
                        isClearable
                        placeholder="All Categories"
                      />
                    </div>
                  </div>

                  {/* Brand */}
                  <div className="col-lg-2">
                    <div className="mb-3">
                      <label className="form-label">Brand</label>
                      <Select
                        options={brands}
                        value={brands.find(b => b.value === filters.brand) || null}
                        onChange={handleBrandChange}
                        isClearable
                        placeholder="All Brands"
                      />
                    </div>
                  </div>

                  {/* Stock Status — client-side only, no API call */}
                  <div className="col-lg-2">
                    <div className="mb-3">
                      <label className="form-label">Stock Status</label>
                      <Select
                        options={stockStatusOptions}
                        value={stockStatusOptions.find(s => s.value === filters.stockStatus)}
                        onChange={handleStockStatusChange}
                      />
                    </div>
                  </div>

                  {/* Sort */}
                  <div className="col-lg-3">
                    <div className="mb-3">
                      <label className="form-label">Sort By</label>
                      <Select
                        options={sortOptions}
                        value={sortOptions.find(s => s.value === sortOption)}
                        onChange={handleSortChange}
                      />
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-12">
                    <button className="btn btn-primary me-2" onClick={() => loadStockSummary(1)}>
                      Apply Filters
                    </button>
                    <button className="btn btn-secondary" onClick={handleReset}>
                      Reset
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* ── Stock Table ─────────────────────────────────────────────────── */}
            <div className="card">
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Product</th>
                        <th>SKU</th>
                        <th>Category</th>
                        <th>Brand</th>
                        <th>Unit</th>
                        <th className="text-end">Current Stock</th>
                        <th className="text-end">Cost Price</th>
                        <th className="text-end">Selling Price</th>
                        <th className="text-end">Stock Value</th>
                        <th>Status</th>
                        <th className="text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="12" className="text-center py-4">
                            <div className="spinner-border text-primary" role="status">
                              <span className="visually-hidden">Loading…</span>
                            </div>
                          </td>
                        </tr>
                      ) : filteredProducts.length === 0 ? (
                        <tr>
                          <td colSpan="12" className="text-center py-4">
                            <p className="text-muted mb-0">
                              {filters.stockStatus !== 'all'
                                ? `No products with status: ${
                                    stockStatusOptions.find(s => s.value === filters.stockStatus)?.label
                                  }`
                                : 'No products found'}
                            </p>
                          </td>
                        </tr>
                      ) : (
                        filteredProducts.map((product, index) => {
                          const status = getStockStatusBadge(product);
                          return (
                            <tr key={product.id}>
                              <td>{((pagination.page - 1) * pagination.limit) + index + 1}</td>
                              <td>
                                <Link to={`/product/${product.id}`} className="fw-bold">
                                  {product.product_name}
                                </Link>
                              </td>
                              <td><span className="badge bg-light text-dark">{product.sku}</span></td>
                              <td>{product.category_name || '—'}</td>
                              <td>{product.brand_name    || '—'}</td>
                              <td>{product.unit          || 'Pcs'}</td>
                              <td className="text-end fw-bold">{product.current_stock || 0}</td>
                              <td className="text-end">{formatCurrency(product.cost_price)}</td>
                              <td className="text-end">{formatCurrency(product.selling_price)}</td>
                              <td className="text-end text-primary fw-bold">
                                {formatCurrency((product.current_stock || 0) * (product.cost_price || 0))}
                              </td>
                              <td>
                                <span className={`badge ${status.class}`}>
                                  {status.icon}{status.text}
                                </span>
                              </td>
                              <td className="text-center">
                                <button
                                  className="btn btn-sm btn-outline-info me-1"
                                  onClick={() => handleViewMovements(product)}
                                  title="View Movements"
                                >
                                  <Eye size={14} />
                                </button>
                                <button
                                  className="btn btn-sm btn-outline-warning"
                                  onClick={() => handleAdjustStock(product)}
                                  title="Adjust Stock"
                                >
                                  <Edit size={14} />
                                </button>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>

                {/* ── Pagination ─────────────────────────────────────────────── */}
                {pagination.total > 0 && (
                  <div className="d-flex justify-content-between align-items-center mt-3">
                    <div className="text-muted small">
                      Showing{' '}
                      <strong>{((pagination.page - 1) * pagination.limit) + 1}</strong>
                      {' '}to{' '}
                      <strong>{Math.min(pagination.page * pagination.limit, pagination.total)}</strong>
                      {' '}of <strong>{pagination.total}</strong> products
                    </div>

                    {pagination.totalPages > 1 && (
                      <nav>
                        <ul className="pagination mb-0">
                          {/* Previous */}
                          <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
                            <button
                              className="page-link"
                              onClick={() => loadStockSummary(pagination.page - 1)}
                              disabled={pagination.page === 1}
                            >
                              Previous
                            </button>
                          </li>

                          {/* Page numbers — sliding window of 5 */}
                          {(() => {
                            const total = pagination.totalPages;
                            const cur   = pagination.page;
                            let start   = Math.max(1, cur - 2);
                            let end     = Math.min(total, start + 4);
                            if (end - start < 4) start = Math.max(1, end - 4);
                            return Array.from({ length: end - start + 1 }, (_, i) => start + i).map(pageNum => (
                              <li key={pageNum} className={`page-item ${cur === pageNum ? 'active' : ''}`}>
                                <button
                                  className="page-link"
                                  onClick={() => loadStockSummary(pageNum)}
                                >
                                  {pageNum}
                                </button>
                              </li>
                            ));
                          })()}

                          {/* Next */}
                          <li className={`page-item ${pagination.page === pagination.totalPages ? 'disabled' : ''}`}>
                            <button
                              className="page-link"
                              onClick={() => loadStockSummary(pagination.page + 1)}
                              disabled={pagination.page === pagination.totalPages}
                            >
                              Next
                            </button>
                          </li>
                        </ul>
                      </nav>
                    )}
                  </div>
                )}
              </div>
            </div>
          </>

        ) : (
          /* ══ Stock Movements View ══════════════════════════════════════════════ */
          <div className="card">
            <div className="card-header bg-light">
              <h5 className="mb-0">{selectedProduct?.product_name} — Stock Movements</h5>
            </div>
            <div className="card-body">

              {/* Product info strip */}
              <div className="row mb-4">
                {[
                  { label: 'SKU',           value: selectedProduct?.sku },
                  { label: 'Current Stock', value: `${selectedProduct?.current_stock} ${selectedProduct?.unit}` },
                  { label: 'Cost Price',    value: formatCurrency(selectedProduct?.cost_price) },
                  { label: 'Selling Price', value: formatCurrency(selectedProduct?.selling_price) }
                ].map(({ label, value }) => (
                  <div className="col-md-3" key={label}>
                    <div className="card bg-light">
                      <div className="card-body">
                        <small className="text-muted d-block">{label}</small>
                        <strong>{value}</strong>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Movement filters */}
              <div className="row mb-4">
                <div className="col-md-4">
                  <label className="form-label">Date Range</label>
                  <div className="d-flex gap-2">
                    <DatePicker
                      className="form-control"
                      value={filters.dateFrom}
                      onChange={(date) => {
                        const newFilters = { ...filtersRef.current, dateFrom: date };
                        setFilters(newFilters);
                        filtersRef.current = newFilters;
                      }}
                      format="DD-MM-YYYY"
                      placeholder="From Date"
                    />
                    <DatePicker
                      className="form-control"
                      value={filters.dateTo}
                      onChange={(date) => {
                        const newFilters = { ...filtersRef.current, dateTo: date };
                        setFilters(newFilters);
                        filtersRef.current = newFilters;
                      }}
                      format="DD-MM-YYYY"
                      placeholder="To Date"
                    />
                  </div>
                </div>

                <div className="col-md-4">
                  <label className="form-label">Movement Type</label>
                  <Select
                    options={movementTypeOptions}
                    value={movementTypeOptions.find(m => m.value === filters.movementType)}
                    onChange={(opt) => {
                      const newFilters = { ...filtersRef.current, movementType: opt?.value || 'all' };
                      setFilters(newFilters);
                      filtersRef.current = newFilters;
                    }}
                  />
                </div>

                <div className="col-md-4 d-flex align-items-end">
                  <button
                    className="btn btn-primary me-2"
                    onClick={() => loadStockMovements(selectedProduct?.id)}
                  >
                    Apply Filters
                  </button>
                  <button
                    className="btn btn-secondary"
                    onClick={() => {
                      const newFilters = { ...filtersRef.current, dateFrom: null, dateTo: null, movementType: 'all' };
                      setFilters(newFilters);
                      filtersRef.current = newFilters;
                      loadStockMovements(selectedProduct?.id);
                    }}
                  >
                    Reset
                  </button>
                </div>
              </div>

              {/* Movements table */}
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Date</th>
                      <th>Type</th>
                      <th className="text-end">Quantity</th>
                      <th className="text-end">Stock Before</th>
                      <th className="text-end">Stock After</th>
                      <th>Reference</th>
                      <th>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan="8" className="text-center py-4">
                          <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading…</span>
                          </div>
                        </td>
                      </tr>
                    ) : stockMovements.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="text-center py-4">
                          <p className="text-muted mb-0">No stock movements found</p>
                        </td>
                      </tr>
                    ) : (
                      stockMovements.map((movement, index) => {
                        const badge = getMovementTypeBadge(movement.type);
                        return (
                          <tr key={movement.id}>
                            <td>{index + 1}</td>
                            <td>{formatDate(movement.created_at)}</td>
                            <td>
                              <span className={`badge ${badge.class}`}>
                                {badge.icon} {badge.text}
                              </span>
                            </td>
                            <td className={`text-end fw-bold ${movement.quantity > 0 ? 'text-success' : 'text-danger'}`}>
                              {movement.quantity > 0 ? '+' : ''}{movement.quantity}
                            </td>
                            <td className="text-end">{movement.stock_before}</td>
                            <td className="text-end">{movement.stock_after}</td>
                            <td>
                              {movement.reference_no
                                ? <span className="badge bg-light text-dark">{movement.reference_no}</span>
                                : '—'}
                            </td>
                            <td>
                              <span className="text-muted small" title={movement.notes}>
                                {movement.notes?.length > 30
                                  ? `${movement.notes.substring(0, 30)}…`
                                  : movement.notes || '—'}
                              </span>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StockManagement;